import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { NewsCardLinkProps } from '@/features/publicsite/types/news/NewsCardLinkProps';
import { formatDate } from '@/features/common/formatDate';
import { useCategoryLabels } from '@/features/common/useCategoryLabels';
import { newsCoverUrl } from '@/features/common/newsCoverUrl';

/**
 * One story in a grid of them — /stiri and the home page's news strip, which drew the same card
 * from two copies of the same markup until both had to change at once.
 *
 * The picture is 16:9 and the category rides on it as a chip, so what is left under it is the
 * headline and the date: a shorter card, and more stories on a screen before the reader scrolls.
 *
 * An <img> rather than the background-image span this used to be, for object-fit and for lazy
 * loading — a grid of twelve covers is most of the page's weight. The one thing the span did
 * better, degrading silently when /img/news/{slug}.jpg does not exist, is kept by hiding the
 * image on error: the panel behind it is the same colour the span showed.
 */
export function NewsCardLink({
  item,
  defaultCategory,
  language,
  variant = 'default',
}: NewsCardLinkProps) {
  const [coverMissing, setCoverMissing] = useState(false);
  const categoryName = useCategoryLabels();
  // Set only when the story's language differs from the page's, so a screen reader switches voice
  // for it (WCAG 3.1.2); on a Romanian page every card matches and the attribute is left off.
  const lang =
    item.contentLanguage.toLowerCase() !== language
      ? item.contentLanguage.toLowerCase()
      : undefined;

  return (
    <Link
      className={`news-card${variant === 'compact' ? ' news-card--compact' : ''}`}
      to={`/stiri/${item.slug}`}
    >
      <span className="news-card__media">
        {!coverMissing && (
          // alt="": the link is named by the headline below; the picture adds nothing a screen
          // reader should announce before it.
          <img
            className="news-card__img"
            src={newsCoverUrl(item)}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setCoverMissing(true)}
          />
        )}
        <span className="cover-chip">
          {item.category ? categoryName(item.category) : defaultCategory}
        </span>
      </span>
      <span className="news-card__body">
        <span className="news-card__title" lang={lang}>
          {item.title}
        </span>
        <time className="news-card__date" dateTime={item.publishedAt}>
          {formatDate(item.publishedAt, language)}
        </time>
      </span>
    </Link>
  );
}
